import type mongoose from "mongoose";
import { SensorPortCommunicationMode } from "../../Configurations/portConfigs.js";
import { sensorClusterConfigs } from "../../Configurations/SensorConfigurations/sensorClusters/sensorClusterConfigs.js";
import { ForeignLookup } from "../commonSubSchemas/foreignLookup.js";
import { SensorClusterAddress } from "../commonSubSchemas/sensorClusterAddress.js";
import {
    SensorCluster,
    type SensorClusterConfigKey,
    type SensorClusterOrigin,
} from "../sensorCluster/sensorCluster.js";
import { THIRD_PARTY_DEVICE_COLLECTION_NAME, type ThirdPartyDevice } from "./thirdPartyDevice.js";

export { getDisplayId } from "../commonGetters/getDisplayId.js";
export { getDisplayName } from "../commonGetters/getDisplayName.js";

/**
 * Builds the warning context object used when posting or clearing warnings for this device.
 *
 * `foreignLookup` identifies the device document in the warnings collection by `node_id`.
 * `targetInfo` carries human-readable metadata shown in warning notifications.
 * `owner` is used to scope warnings to the correct user account.
 */
export const getWarningContext = (self: ThirdPartyDevice) => ({
    foreignLookup: new ForeignLookup({
        targetId: self.node_id,
        targetForeignField: "node_id",
        targetCollection: THIRD_PARTY_DEVICE_COLLECTION_NAME,
    }),
    targetInfo: {
        name: self.name ?? self.node_id,
        node_id: self.node_id,
        _id: (self as ThirdPartyDevice & { _id: mongoose.Types.ObjectId })._id,
    },
    owner: self.owner,
    aoi: self._aoi,
});

/**
 * Derives the SensorCluster documents that correspond to this ThirdPartyDevice.
 *
 * - For ePlant TreeTag devices, returns the main fixed cluster on port I1 (DIGITAL, channel 0)
 *   plus its battery cluster on the diagnostic port D1.
 * - For SDI-12 node devices, returns a cluster on port S1 derived from the device's SDI12NodeType.
 *
 * Returns an empty array if the device has no owner, or (for SDI-12 devices) if no matching
 * cluster config is found.
 */
export const getSensorClusters = (device: ThirdPartyDevice): SensorCluster[] => {
    if (!device.owner) return [];

    const hostReference = new ForeignLookup({
        targetId: device.node_id,
        targetForeignField: "node_id",
        targetCollection: THIRD_PARTY_DEVICE_COLLECTION_NAME,
    });

    if (device.deviceType === "ePlantTreeTag") {
        const cluster = new SensorCluster();
        Object.assign(cluster, {
            address: new SensorClusterAddress({
                hostReference,
                portId: "I1",
                communicationMode: SensorPortCommunicationMode.DIGITAL,
                channel: 0,
            }),
            sensorClusterConfigKey: "ePlantTreeTag" as SensorClusterConfigKey,
            // I1 is not declared in this device type's sensorPortConfiguration; the origin is
            // asserted from device-type semantics — the TreeTag sensor is built into the device.
            origin: "fixedDevicePort" satisfies SensorClusterOrigin,
            parameters: {},
            owner: device.owner,
            connectedZones: device.connectedZones ?? [],
            position: device.position,
        });

        const batteryCluster = new SensorCluster();
        Object.assign(batteryCluster, {
            address: new SensorClusterAddress({
                hostReference,
                portId: "D1",
                communicationMode: SensorPortCommunicationMode.DIGITAL,
                channel: 0,
            }),
            sensorClusterConfigKey: "EPlantTreeTag_Battery" as SensorClusterConfigKey,
            origin: "fixedDevicePort" satisfies SensorClusterOrigin,
            parameters: {},
            owner: device.owner,
            connectedZones: device.connectedZones ?? [],
            position: device.position,
        });

        return [cluster, batteryCluster];
    }

    const SDI12NodeType = device.deviceTypeSpecificInformation?.SDI12NodeType as string | undefined;
    if (!SDI12NodeType) return [];

    const matchingEntry = Object.entries(sensorClusterConfigs).find(
        ([, config]) => config.sdi12NodeTypeKey === SDI12NodeType,
    );
    if (!matchingEntry) return [];

    const [sensorClusterConfigKey] = matchingEntry;

    const cluster = new SensorCluster();
    Object.assign(cluster, {
        address: new SensorClusterAddress({
            hostReference,
            // Always S1: the Dragino SDI-12 node device configuration defines a single sensor port named S1.
            portId: "S1",
            communicationMode: SensorPortCommunicationMode.SDI12,
            channel: 0,
        }),
        sensorClusterConfigKey: sensorClusterConfigKey as SensorClusterConfigKey,
        // The Dragino SDI-12 port is user-wired (its catalogue entry is `isFixed: false`).
        origin: "userConfigured" satisfies SensorClusterOrigin,
        parameters: {},
        owner: device.owner,
        connectedZones: device.connectedZones ?? [],
        position: device.position,
    });

    return [cluster];
};
